<script setup>
import { storeToRefs } from 'pinia'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useAuthStore } from '~/stores/auth'

definePageMeta({
    pageName: 'shopDetail'
})

// Shop name
const route = useRoute()
const shopUid = computed(() => route?.params?.shopUid)
const centerRoute = computed(() => route.params?.centerUid)

// Fetch center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

// Fetch shop details
const { data: shop, suspense: shopDetailSuspense, isPlaceholderData: isShopDetailPlaceholderData } = useFetchShopDetail(shopUid.value)

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerDetailSuspense() // Wait for center details to finish fetching
    await shopDetailSuspense() // Wait for shop details to finish fetching
})

// Auth
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.SHOP_DETAILS,
    centerUid: centerDetails.value?.centerUid,
    loginStatus: isLoggedIn.value ? TRACKING_CONSTANTS.MISC.LOGGED_IN : TRACKING_CONSTANTS.MISC.LOGGED_OUT,
    shopName: shop.value?.name
})

// Seo
useSeoMeta({
    title: () => `${shop.value?.name} | ${centerDetails.value?.centerName}`
})
</script>

<template>
    <PageMainContainer :page-main-container-config="{ appFooter: true }">
        <ShopHeader :shop-header="{ name: shop.name, logoType: shop.logotype, placeholder: isShopDetailPlaceholderData }" />

        <template v-if="isShopDetailPlaceholderData">
            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h1' }" />
            <br>
            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text', iterator: 3 }" />
        </template>

        <template v-else>
            <ShopCoupons v-if="shop?.hasCoupons" :shop-name="shop.name" />
            <ShopLoyalty v-if="shop?.hasLoyaltyProgram" :shop-name="shop.name" />
            <ShopSummary :shop-summary="{ name: shop?.name, hours: shop?.hours, contacts: shop?.contacts }" />
            <ShopCenterPlan v-if="shop.location?.centerPlan?.link" :center-plan="shop.location?.centerPlan" />
            <ShopDescription v-if="shop?.description" :description="shop.description" />
        </template>
    </PageMainContainer>
</template>
